import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IS_ANDROID_APP } from "../config/appTarget.js";

/**
 * Handles the Android hardware/gesture back button.
 *
 * Without this, pressing back in a Capacitor SPA exits the app immediately
 * because the WebView history is always empty (React Router manages navigation
 * in-memory, not via real WebView history entries).
 *
 * We use window.history.state.idx (set by React Router's BrowserRouter) rather
 * than the event's canGoBack field — canGoBack reflects WebView history (always
 * false in a SPA), whereas idx reflects the React Router navigation stack.
 */
export function useAndroidBackButton(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!IS_ANDROID_APP) return;

    let cleanup: (() => void) | undefined;

    import("@capacitor/app").then(({ App: CapApp }) => {
      const listenerPromise = CapApp.addListener("backButton", () => {
        if ((window.history.state?.idx ?? 0) > 0) {
          navigate(-1);
        } else {
          CapApp.exitApp();
        }
      });

      listenerPromise.then((handle) => {
        cleanup = () => handle.remove();
      });
    });

    return () => {
      cleanup?.();
    };
  }, [navigate]);
}
